"use client";

import { useRef, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import ApplicationAddressField, { type ApplicationAddressValue } from "./ApplicationAddressField";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = ["application/pdf", "image/png", "image/jpeg"] as const;

type AllowedMime = (typeof ALLOWED_MIMES)[number];

type FormDataState = {
  legal_business_name: string;
  dba_name: string;
  business_type: string;
  business_address: string;
  business_address_line_1: string;
  business_address_line_2: string;
  business_address_city: string;
  business_address_state: string;
  business_address_postal_code: string;
  business_address_country_code: string;
  business_address_source: "google_places" | "manual";
  business_address_place_id: string;
  business_address_verification_token: string;
  contact_person: string;
  email: string;
  phone: string;
  state_license_number: string;
  state_license_expiration_date: string;
  dea_number: string;
  dea_expiration_date: string;
  npi_number: string;
};

type FileFieldName =
  | "state_license_file"
  | "dea_license_file"
  | "w9_file"
  | "resale_certificate_file"
  | "other_document_file";

type FileState = Record<FileFieldName, File | null>;
type FileErrorState = Partial<Record<FileFieldName, string>>;
type UploadStatus = "waiting" | "uploading" | "uploaded" | "error";
type UploadStatusState = Partial<Record<FileFieldName, UploadStatus>>;

type UploadAuthorization = {
  slot: FileFieldName;
  label: string;
  path: string;
  mime: AllowedMime;
  size: number;
  token: string;
};

const initialFormData: FormDataState = {
  legal_business_name: "",
  dba_name: "",
  business_type: "",
  business_address: "",
  business_address_line_1: "",
  business_address_line_2: "",
  business_address_city: "",
  business_address_state: "",
  business_address_postal_code: "",
  business_address_country_code: "US",
  business_address_source: "manual",
  business_address_place_id: "",
  business_address_verification_token: "",
  contact_person: "",
  email: "",
  phone: "",
  state_license_number: "",
  state_license_expiration_date: "",
  dea_number: "",
  dea_expiration_date: "",
  npi_number: "",
};

const initialFiles: FileState = {
  state_license_file: null,
  dea_license_file: null,
  w9_file: null,
  resale_certificate_file: null,
  other_document_file: null,
};

const documentFields: Array<{
  name: FileFieldName;
  label: string;
  required?: boolean;
}> = [
  {
    name: "state_license_file",
    label: "State pharmacy license / state business license",
    required: true,
  },
  { name: "dea_license_file", label: "DEA registration" },
  { name: "w9_file", label: "W-9" },
  {
    name: "resale_certificate_file",
    label: "Resale / tax exemption certificate",
  },
  { name: "other_document_file", label: "Other onboarding document" },
];

function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function inferMime(file: File): AllowedMime | null {
  if (ALLOWED_MIMES.includes(file.type as AllowedMime)) {
    return file.type as AllowedMime;
  }

  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith(".pdf")) return "application/pdf";
  if (lowerName.endsWith(".png")) return "image/png";
  if (lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg")) return "image/jpeg";
  return null;
}

function normalizeFile(file: File) {
  const mime = inferMime(file);

  if (!mime) {
    return { file: null, mime: null, error: "Upload a PDF, PNG, JPG, or JPEG file." };
  }

  if (file.size <= 0) {
    return { file: null, mime: null, error: "The selected file appears to be empty." };
  }

  if (file.size > MAX_FILE_BYTES) {
    return { file: null, mime: null, error: "File size must be 10 MB or less." };
  }

  if (file.type === mime) {
    return { file, mime, error: "" };
  }

  return {
    file: new File([file], file.name, { type: mime, lastModified: file.lastModified }),
    mime,
    error: "",
  };
}

function normalizeMetadata(formData: FormDataState): FormDataState {
  return {
    legal_business_name: formData.legal_business_name.trim(),
    dba_name: formData.dba_name.trim(),
    business_type: formData.business_type.trim(),
    business_address: formData.business_address.trim(),
    business_address_line_1: formData.business_address_line_1.trim(),
    business_address_line_2: formData.business_address_line_2.trim(),
    business_address_city: formData.business_address_city.trim(),
    business_address_state: formData.business_address_state.trim().toUpperCase(),
    business_address_postal_code: formData.business_address_postal_code.trim(),
    business_address_country_code: "US",
    business_address_source: formData.business_address_source,
    business_address_place_id: formData.business_address_place_id.trim(),
    business_address_verification_token: formData.business_address_verification_token.trim(),
    contact_person: formData.contact_person.trim(),
    email: formData.email.trim(),
    phone: formData.phone.trim(),
    state_license_number: formData.state_license_number.trim(),
    state_license_expiration_date: formData.state_license_expiration_date,
    dea_number: formData.dea_number.trim(),
    dea_expiration_date: formData.dea_expiration_date,
    npi_number: formData.npi_number.trim(),
  };
}

function isUploadAuthorization(value: unknown): value is UploadAuthorization {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.slot === "string" &&
    documentFields.some((field) => field.name === candidate.slot) &&
    typeof candidate.label === "string" &&
    typeof candidate.path === "string" &&
    typeof candidate.mime === "string" &&
    ALLOWED_MIMES.includes(candidate.mime as AllowedMime) &&
    typeof candidate.size === "number" &&
    typeof candidate.token === "string"
  );
}

async function readJson(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function InputField({
  label,
  name,
  value,
  type = "text",
  required = false,
  autoComplete,
  onChange,
}: {
  label: string;
  name: keyof FormDataState;
  value: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <label className="grid gap-2 text-sm font-semibold text-slate-800">
      <span>
        {label}
        {required && <span className="ml-1 text-red-600">*</span>}
      </span>
      <input
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        type={type}
        autoComplete={autoComplete}
        className="min-h-12 rounded-md border border-[var(--vintera-border)] bg-white px-3 py-2 text-base font-normal text-[var(--vintera-ink)] outline-none transition focus:border-[var(--vintera-navy)] focus:ring-2 focus:ring-[#092a5a]/15"
      />
    </label>
  );
}

function FileUploadField({
  name,
  label,
  required = false,
  selectedFile,
  error,
  status,
  disabled,
  onFile,
}: {
  name: FileFieldName;
  label: string;
  required?: boolean;
  selectedFile: File | null;
  error?: string;
  status?: UploadStatus;
  disabled: boolean;
  onFile: (name: FileFieldName, file: File | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const statusLabel =
    status === "uploading"
      ? "Uploading"
      : status === "uploaded"
        ? "Uploaded"
        : status === "error"
          ? "Needs attention"
          : selectedFile
            ? "Ready"
            : "Waiting";

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (disabled) return;
    onFile(name, event.dataTransfer.files?.[0] ?? null);
  }

  return (
    <div className="rounded-md border border-[var(--vintera-border)] bg-[var(--vintera-sage-soft)] p-4">
      <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
        <div>
          <p className="text-sm font-semibold text-[var(--vintera-navy)]">
            {label}
            {required && <span className="ml-1 text-red-600">*</span>}
          </p>
          <p className="mt-1 text-sm text-slate-600">PDF, PNG, JPG, or JPEG up to 10 MB.</p>
        </div>
        <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
          {statusLabel}
        </span>
      </div>

      <div
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        className="mt-4 flex flex-col gap-3 rounded-md border border-dashed border-[var(--vintera-sage)] bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-800">
            {selectedFile ? selectedFile.name : "No file selected"}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {selectedFile ? formatBytes(selectedFile.size) : "Drag a file here or choose one."}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            disabled={disabled}
            aria-label={`Choose ${label}`}
            onClick={() => inputRef.current?.click()}
            className="rounded-md bg-[var(--vintera-navy)] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[var(--vintera-navy-hover)] disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            Choose File
          </button>
          {selectedFile && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onFile(name, null)}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              Remove
            </button>
          )}
        </div>

        <input
          ref={inputRef}
          name={name}
          aria-label={`${label} file`}
          onChange={(event) => onFile(name, event.target.files?.[0] ?? null)}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
          className="sr-only"
          disabled={disabled}
        />
      </div>

      {error && <p className="mt-3 text-sm font-medium text-red-700">{error}</p>}
    </div>
  );
}

export default function ApplyPage() {
  const [formData, setFormData] = useState<FormDataState>(initialFormData);
  const [files, setFiles] = useState<FileState>(initialFiles);
  const [fileErrors, setFileErrors] = useState<FileErrorState>({});
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatusState>({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"info" | "success" | "error">("info");
  const [phase, setPhase] = useState("Not submitted");

  function handleInputChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  }

  function handleFile(name: FileFieldName, file: File | null) {
    setUploadStatuses((current) => ({ ...current, [name]: file ? "waiting" : undefined }));

    if (!file) {
      setFiles((current) => ({ ...current, [name]: null }));
      setFileErrors((current) => ({ ...current, [name]: "" }));
      return;
    }

    const normalized = normalizeFile(file);
    if (normalized.error || !normalized.file) {
      setFiles((current) => ({ ...current, [name]: null }));
      setFileErrors((current) => ({ ...current, [name]: normalized.error }));
      return;
    }

    setFiles((current) => ({ ...current, [name]: normalized.file }));
    setFileErrors((current) => ({ ...current, [name]: "" }));
  }

  function validateClientSubmission() {
    const normalizedMetadata = normalizeMetadata(formData);

    if (!normalizedMetadata.legal_business_name) return "Enter the legal business name.";
    if (!normalizedMetadata.business_type) return "Select a business type.";
    if (!normalizedMetadata.business_address_line_1 || !normalizedMetadata.business_address_city || normalizedMetadata.business_address_state.length !== 2 || !/^\d{5}(?:-\d{4})?$/.test(normalizedMetadata.business_address_postal_code)) return "Enter a complete structured United States business address.";
    if (!normalizedMetadata.contact_person) return "Enter the primary contact person.";
    if (!normalizedMetadata.email) return "Enter an email address.";
    if (!normalizedMetadata.phone) return "Enter a phone number.";
    if (!normalizedMetadata.state_license_number || !normalizedMetadata.state_license_expiration_date) return "Enter the state license number and expiration date.";
    if (normalizedMetadata.dea_number && !normalizedMetadata.dea_expiration_date) return "Enter the DEA expiration date when a DEA number is provided.";
    if (!normalizedMetadata.dea_number && normalizedMetadata.dea_expiration_date) return "Enter the DEA number for the expiration date.";
    if (!files.state_license_file) {
      return "Upload the state pharmacy license or state business license.";
    }

    const activeFileErrors = Object.values(fileErrors).filter(Boolean);
    if (activeFileErrors.length > 0) return activeFileErrors[0];

    return "";
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const formElement = event.currentTarget;

    const clientError = validateClientSubmission();
    if (clientError) {
      setMessage(clientError);
      setMessageType("error");
      return;
    }

    const metadata = normalizeMetadata(formData);
    const selectedFiles = documentFields
      .map((field) => ({ ...field, file: files[field.name] }))
      .filter((field): field is (typeof field) & { file: File } => Boolean(field.file));

    let issuedToken = "";

    setSubmitting(true);
    setMessage("");
    setMessageType("info");
    setPhase("Preparing secure upload");
    setUploadStatuses(
      selectedFiles.reduce<UploadStatusState>(
        (current, field) => ({ ...current, [field.name]: "waiting" }),
        {}
      )
    );

    try {
      const initResponse = await fetch("/api/applications/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          metadata,
          files: selectedFiles.map(({ name, file }) => ({
            slot: name,
            name: file.name,
            mime: inferMime(file),
            size: file.size,
          })),
        }),
      });

      const initResult = await readJson(initResponse);
      if (
        !initResponse.ok ||
        !initResult ||
        typeof initResult !== "object" ||
        typeof (initResult as Record<string, unknown>).token !== "string" ||
        !Array.isArray((initResult as Record<string, unknown>).uploads)
      ) {
        throw new Error("init failed");
      }

      issuedToken = (initResult as { token: string }).token;
      const uploads = (initResult as { uploads: unknown[] }).uploads;
      const uploadAuthorizations = uploads.filter(isUploadAuthorization);

      if (uploadAuthorizations.length !== selectedFiles.length) {
        throw new Error("upload authorization mismatch");
      }

      for (const upload of uploadAuthorizations) {
        const file = files[upload.slot];
        if (!file) throw new Error("file missing");

        setPhase(`Uploading ${upload.label}`);
        setUploadStatuses((current) => ({ ...current, [upload.slot]: "uploading" }));

        const { error } = await supabase.storage
          .from("application-documents")
          .uploadToSignedUrl(upload.path, upload.token, file, {
            cacheControl: "3600",
            contentType: upload.mime,
            upsert: false,
          });

        if (error) {
          setUploadStatuses((current) => ({ ...current, [upload.slot]: "error" }));
          throw new Error("upload failed");
        }

        setUploadStatuses((current) => ({ ...current, [upload.slot]: "uploaded" }));
      }

      setPhase("Finalizing application");
      const finalizeResponse = await fetch("/api/applications/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: issuedToken, metadata }),
      });

      if (!finalizeResponse.ok) {
        throw new Error("finalize failed");
      }

      setFormData(initialFormData);
      setFiles(initialFiles);
      setFileErrors({});
      setUploadStatuses({});
      setPhase("Submitted");
      setMessageType("success");
      setMessage(
        "Application submitted successfully. Vintera Health will review the account request and documents."
      );
      formElement.reset();
    } catch {
      let retryFinalized = false;

      if (issuedToken) {
        try {
          const retryResponse = await fetch("/api/applications/finalize", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: issuedToken, metadata }),
          });
          retryFinalized = retryResponse.ok;
        } catch {
          // Best-effort cleanup is attempted through finalize; user-facing copy stays generic.
        }
      }

      if (retryFinalized) {
        setFormData(initialFormData);
        setFiles(initialFiles);
        setFileErrors({});
        setUploadStatuses({});
        setPhase("Submitted");
        setMessageType("success");
        setMessage(
          "Application submitted successfully. Vintera Health will review the account request and documents."
        );
        formElement.reset();
        return;
      }

      setPhase("Needs attention");
      setMessageType("error");
      setMessage(
        "We could not submit the application. Please check the fields and documents, then try again."
      );
      console.error("Application submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  const messageClass =
    messageType === "success"
      ? "border-green-200 bg-green-50 text-green-900"
      : messageType === "error"
        ? "border-red-200 bg-red-50 text-red-900"
        : "border-[var(--vintera-border)] bg-[var(--vintera-sage-soft)] text-[var(--vintera-navy)]";

  return (
    <main className="min-h-screen bg-white text-[var(--vintera-ink)]">
      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:py-14">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase text-[var(--vintera-sage-dark)]">Wholesale account request</p>
          <h1 className="mt-3 text-3xl font-bold text-[var(--vintera-navy)] sm:text-5xl">
            Apply for a Vintera Health account
          </h1>
          <p className="mt-4 text-base leading-7 text-[var(--vintera-muted)] sm:text-lg">
            Submit business details and onboarding documents for review. Your documents are
            uploaded securely and are available only to authorized reviewers.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <section className="rounded-md border border-[var(--vintera-border)] bg-white p-5 sm:p-6">
            <div className="flex flex-col gap-2 border-b border-[var(--vintera-border)] pb-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[var(--vintera-navy)]">Business Information</h2>
                <p className="mt-1 text-sm text-slate-600">Fields marked with * are required.</p>
              </div>
              <span className="w-fit rounded-full bg-[var(--vintera-sage-soft)] px-3 py-1 text-xs font-semibold text-[var(--vintera-sage-dark)] ring-1 ring-[var(--vintera-border)]">
                {phase}
              </span>
            </div>

            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <InputField
                label="Legal business name"
                name="legal_business_name"
                value={formData.legal_business_name}
                onChange={handleInputChange}
                required
                autoComplete="organization"
              />
              <InputField
                label="DBA name"
                name="dba_name"
                value={formData.dba_name}
                onChange={handleInputChange}
                autoComplete="organization"
              />
              <label className="grid gap-2 text-sm font-semibold text-slate-800">
                <span>
                  Business type<span className="ml-1 text-red-600">*</span>
                </span>
                <select
                  name="business_type"
                  value={formData.business_type}
                  onChange={handleInputChange}
                  required
                  className="min-h-12 rounded-md border border-[var(--vintera-border)] bg-white px-3 py-2 text-base font-normal text-[var(--vintera-ink)] outline-none transition focus:border-[var(--vintera-navy)] focus:ring-2 focus:ring-[#092a5a]/15"
                >
                  <option value="">Select a business type</option>
                  <option value="Pharmacy">Pharmacy</option>
                  <option value="Medical office">Medical office</option>
                  <option value="Clinic">Clinic</option>
                  <option value="Hospital">Hospital</option>
                  <option value="Other healthcare facility">Other healthcare facility</option>
                </select>
              </label>
              <InputField
                label="Primary contact"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleInputChange}
                required
                autoComplete="name"
              />
              <ApplicationAddressField disabled={submitting} value={formData as ApplicationAddressValue} onChange={(address) => setFormData((current) => ({ ...current, ...address }))} />
              <InputField
                label="Email address"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                type="email"
                autoComplete="email"
              />
              <InputField
                label="Phone number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                type="tel"
                autoComplete="tel"
              />
            </div>
          </section>

          <section className="rounded-md border border-[var(--vintera-border)] bg-white p-5 sm:p-6">
            <h2 className="text-xl font-semibold text-[var(--vintera-navy)]">Licensing Information</h2>
            <p className="mt-1 text-sm text-slate-600">
              State license information is required. DEA information remains optional, but its expiration is required when a DEA number is supplied.
            </p>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              <InputField
                label="State license number"
                name="state_license_number"
                value={formData.state_license_number}
                onChange={handleInputChange}
                required
              />
              <InputField
                label="State license expiration"
                name="state_license_expiration_date"
                value={formData.state_license_expiration_date}
                onChange={handleInputChange}
                type="date"
                required
              />
              <InputField
                label="DEA number"
                name="dea_number"
                value={formData.dea_number}
                onChange={handleInputChange}
              />
              <InputField
                label="DEA expiration"
                name="dea_expiration_date"
                value={formData.dea_expiration_date}
                onChange={handleInputChange}
                type="date"
              />
              <InputField
                label="NPI number"
                name="npi_number"
                value={formData.npi_number}
                onChange={handleInputChange}
              />
            </div>
          </section>

          <section className="rounded-md border border-[var(--vintera-border)] bg-white p-5 sm:p-6">
            <div className="border-b border-[var(--vintera-border)] pb-5">
              <h2 className="text-xl font-semibold text-[var(--vintera-navy)]">Documents</h2>
              <p className="mt-1 text-sm text-slate-600">
                The state pharmacy license or state business license is required. Add other
                documents when they apply to your business.
              </p>
            </div>
            <div className="mt-6 grid gap-4">
              {documentFields.map((field) => (
                <FileUploadField
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  required={field.required}
                  selectedFile={files[field.name]}
                  error={fileErrors[field.name]}
                  status={uploadStatuses[field.name]}
                  disabled={submitting}
                  onFile={handleFile}
                />
              ))}
            </div>
          </section>

          {message && (
            <p className={`rounded-lg border p-4 text-sm font-medium ${messageClass}`} aria-live="polite">
              {message}
            </p>
          )}

          <div className="flex flex-col gap-3 rounded-md border border-[var(--vintera-border)] bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-600">
              Submission is disabled while secure document upload is in progress.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="min-h-12 rounded-md bg-[var(--vintera-navy)] px-6 py-3 text-base font-semibold text-white transition hover:bg-[var(--vintera-navy-hover)] disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {submitting ? "Submitting Application" : "Submit Application"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
